// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { ExitDialogButton } from '../../components/common/button';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from '../../lib/base';
import { CUSTOMERS_QUERY } from '../../queries/CustomersQuery';
import type {
  DeleteOrPauseRecurringOrderDataQuery,
  DeleteOrPauseRecurringOrderDataQueryVariables,
  DeleteRecurringOrderMutation,
  DeleteRecurringOrderMutationVariables,
  PauseRecurringOrderMutation,
  PauseRecurringOrderMutationVariables,
} from '../../types/__generated__/graphql';
import { getNumericID } from '../../utils';
import { evictCacheData } from '../../utils/evictCacheData';
import CancellableOrdersList from './CancellableOrdersList';
import { ChildOrdersContainer } from './ChildOrdersContainer';

interface Props {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly recurringOrderId: string;
  readonly isForDelete: boolean; // True if deleting, false if pausing
}

const PAUSE_RECURRING_ORDER_MUTATION = gql`
  mutation PauseRecurringOrder($id: Int!, $in_progress_orders_to_cancel: [Int!]!) {
    pauseRecurringOrder(input: { recurring_order_id: $id, in_progress_orders_to_cancel: $in_progress_orders_to_cancel }) {
      updated_recurring_order {
        id
        status
        updated_at
      }
      deleted_orders {
        id
      }
      deleted_order_inputs {
        id
      }
      deleted_plantings {
        id
      }
      deleted_tasks {
        id
      }
      updated_orders {
        id
        order_status
        updated_at
      }
      updated_tasks {
        id
        order_id
        updated_at
      }
      updated_plantings {
        id
        order_id
        trays_rounded
        planting_state
        updated_at
      }
    }
  }
`;
const DELETE_RECURRING_ORDER_MUTATION = gql`
  mutation DeleteRecurringOrder($id: Int!, $in_progress_orders_to_cancel: [Int!]!) {
    deleteRecurringOrder(input: { recurring_order_id: $id, in_progress_orders_to_cancel: $in_progress_orders_to_cancel }) {
      deleted_recurring_order {
        id
      }
      deleted_orders {
        id
      }
      deleted_order_inputs {
        id
      }
      deleted_plantings {
        id
      }
      deleted_tasks {
        id
      }
      updated_orders {
        id
        order_status
        updated_at
      }
      updated_tasks {
        id
        order_id
        updated_at
      }
      updated_plantings {
        id
        order_id
        trays_rounded
        planting_state
        updated_at
      }
    }
  }
`;
const DELETE_RECURRING_ORDER_DATA = gql`
  query DeleteOrPauseRecurringOrderData($id: ID!) {
    recurring_order(id: $id) {
      id
      completedCancelledOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "in", value: "completed,cancelled" }
        ]
      ) {
        count
      }
      plannedOrders: child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "planned" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
          }
        }
      }
      child_orders(
        filters: [
          { name: "order.order_status", operator: "=", value: "in_progress" }
        ]
        orderBy: [
          { name: "order.order_fulfillment_time", direction: "asc" }
        ]
      ) {
        edges {
          node {
            id
            order_fulfillment_time
            order_status
            plantings {
              edges {
                node {
                  id
                  planting_status
                  trays_rounded
                  yield_per_tray_ounces
                  crop {
                    id
                    crop_name
                    yield_unit
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default function DeleteOrPauseRecurringOrderDialog({
  isOpen,
  onClose,
  onSuccess,
  recurringOrderId,
  isForDelete,
}: Props) {
  const { timezone } = useUserContext();
  const [deleteRecurringOrder, { client, loading: deleteRecurringOrderLoading }] = useMutation<
    DeleteRecurringOrderMutation,
    DeleteRecurringOrderMutationVariables
  >(DELETE_RECURRING_ORDER_MUTATION);
  const [pauseRecurringOrder, { loading: pauseRecurringOrderLoading }] = useMutation<
    PauseRecurringOrderMutation,
    PauseRecurringOrderMutationVariables
  >(PAUSE_RECURRING_ORDER_MUTATION);
  const [getRecurringOrderData, { data: recurringOrderData, loading: recurringOrderDataLoading }] = useLazyQuery<
    DeleteOrPauseRecurringOrderDataQuery,
    DeleteOrPauseRecurringOrderDataQueryVariables
  >(DELETE_RECURRING_ORDER_DATA, {
    variables: { id: recurringOrderId },
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringOrderData({
        variables: { id: recurringOrderId },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, getRecurringOrderData, recurringOrderId]);

  const [selectedCancellableOrders, setSelectedCancellableOrders] = React.useState<number[]>([]);
  const onSetSelectedCancellableOrders = (orders: number[]) => {
    setSelectedCancellableOrders(orders);
  };

  const title = isForDelete ? 'Delete Recurring Order' : 'Pause Recurring Order';
  const description = isForDelete
    ? 'Are you sure you want to delete this recurring order?'
    : 'Are you sure you want to pause this recurring order?';

  const completedOrCancelledCount = recurringOrderData?.recurring_order?.completedCancelledOrders?.count ?? 0;
  const inProgressChildOrders = recurringOrderData?.recurring_order?.child_orders?.edges.map((n) => n.node) ?? [];
  const plannedChildOrders = recurringOrderData?.recurring_order?.plannedOrders?.edges.map((n) => n.node) ?? [];

  const onSubmit = () => {
    if (isForDelete) {
      void deleteRecurringOrder({
        refetchQueries: [{ query: CUSTOMERS_QUERY }],
        variables: { id: getNumericID(recurringOrderId), in_progress_orders_to_cancel: selectedCancellableOrders },
        onCompleted: (completedData) => {
          enqueueSnackbar('Deleted recurring order', { variant: 'success' });
          if (completedData.deleteRecurringOrder != null) {
            evictCacheData({
              apolloClient: client,
              orderIds: completedData.deleteRecurringOrder.deleted_orders.map((o) => o.id),
              orderInputIds: completedData.deleteRecurringOrder.deleted_order_inputs.map((i) => i.id),
              plantingIds: completedData.deleteRecurringOrder.deleted_plantings.map((p) => p.id),
              recurringOrderIds: [completedData.deleteRecurringOrder.deleted_recurring_order.id],
              taskIds: completedData.deleteRecurringOrder.deleted_tasks.map((t) => t.id),
            });
          }
          onSuccess();
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    } else {
      void pauseRecurringOrder({
        variables: { id: getNumericID(recurringOrderId), in_progress_orders_to_cancel: selectedCancellableOrders },
        onCompleted: (completedData) => {
          enqueueSnackbar('Paused recurring order', { variant: 'success' });
          if (completedData.pauseRecurringOrder != null) {
            evictCacheData({
              apolloClient: client,
              orderIds: completedData.pauseRecurringOrder.deleted_orders.map((o) => o.id),
              orderInputIds: completedData.pauseRecurringOrder.deleted_order_inputs.map((i) => i.id),
              plantingIds: completedData.pauseRecurringOrder.deleted_plantings.map((p) => p.id),
              taskIds: completedData.pauseRecurringOrder.deleted_tasks.map((t) => t.id),
            });
          }
          onSuccess();
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  };

  const loading = recurringOrderDataLoading || deleteRecurringOrderLoading || pauseRecurringOrderLoading;
  const submitDisabled = loading;

  const dialogContent = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }}>
      {inProgressChildOrders.length > 0 && (
        <CancellableOrdersList
          selectedOrders={selectedCancellableOrders}
          setSelectedOrders={onSetSelectedCancellableOrders}
          orders={inProgressChildOrders}
          timezone={timezone}
        />
      )}
      {plannedChildOrders.length > 0 && (
        <ChildOrdersContainer
          title="The following planned child orders will be deleted:"
          ordersIn={plannedChildOrders}
        />
      )}
      {completedOrCancelledCount > 0 && (
        <Typography>
          The remaining {completedOrCancelledCount} completed or cancelled child orders will not be affected.
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="sm"
    >
      <DialogContent sx={{ padding: 1 }}>
        <Box sx={{ p: 0 }} display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center">
          <ExitDialogButton onCloseDialog={onClose} />
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography sx={{ mb: 1 }} variant="h4">
              {title}
            </Typography>
            <Typography variant="body1">{description}</Typography>
          </Box>
          {dialogContent}
        </Box>
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          disabled={submitDisabled}
        >
          Cancel
        </Button>
        <Button
          id="submit-order-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={submitDisabled}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
